import {
  type Repair,
  type RepairAdmin,
  type RepairPriority,
  type RepairStatus,
  type RepairStatusEvent,
  getSupabase,
} from "@/lib/supabase";
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "./useAuth";
import { useIsAdmin } from "./useIsAdmin";

type RepairListState<T> = {
  loading: boolean;
  error: string | null;
  repairs: T[];
  refresh: () => Promise<void>;
};

/**
 * Fetch repairs for the current viewer.
 * - Tenant: their own repairs (via `repairs_tenant` view, internal_notes hidden).
 * - Admin: every repair (via `repairs` table) so AdminRepairs can edit.
 */
export function useRepairs(): RepairListState<Repair> {
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, loading: adminLoading } = useIsAdmin();
  const [state, setState] = useState<{
    loading: boolean;
    error: string | null;
    repairs: Repair[];
  }>({ loading: true, error: null, repairs: [] });

  const fetchOnce = useCallback(async () => {
    if (!user) {
      setState({ loading: false, error: null, repairs: [] });
      return;
    }
    setState((s) => ({ ...s, loading: true, error: null }));
    const supabase = getSupabase();
    const source = isAdmin ? "repairs" : "repairs_tenant";
    const { data, error } = (await supabase
      .from(source)
      .select("*")
      .order("created_at", { ascending: false })) as {
      data: Repair[] | null;
      error: { message: string } | null;
    };
    if (error) {
      setState({ loading: false, error: error.message, repairs: [] });
      return;
    }
    setState({ loading: false, error: null, repairs: data ?? [] });
  }, [user, isAdmin]);

  useEffect(() => {
    if (authLoading || adminLoading) return;
    void fetchOnce();
  }, [authLoading, adminLoading, fetchOnce]);

  return { ...state, refresh: fetchOnce };
}

export function useAdminRepairs(): RepairListState<RepairAdmin> {
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, loading: adminLoading } = useIsAdmin();
  const [state, setState] = useState<{
    loading: boolean;
    error: string | null;
    repairs: RepairAdmin[];
  }>({ loading: true, error: null, repairs: [] });

  const fetchOnce = useCallback(async () => {
    if (!user || !isAdmin) {
      setState({ loading: false, error: null, repairs: [] });
      return;
    }
    setState((s) => ({ ...s, loading: true, error: null }));
    const supabase = getSupabase();
    const { data, error } = (await supabase
      .from("repairs")
      .select("*")
      .order("created_at", { ascending: false })) as {
      data: RepairAdmin[] | null;
      error: { message: string } | null;
    };
    if (error) {
      setState({ loading: false, error: error.message, repairs: [] });
      return;
    }
    setState({ loading: false, error: null, repairs: data ?? [] });
  }, [user, isAdmin]);

  useEffect(() => {
    if (authLoading || adminLoading) return;
    void fetchOnce();
  }, [authLoading, adminLoading, fetchOnce]);

  return { ...state, refresh: fetchOnce };
}

/** Fetch the chronological status history for a single repair. */
export function useRepairTimeline(repairId: string | null) {
  const [state, setState] = useState<{
    loading: boolean;
    error: string | null;
    events: RepairStatusEvent[];
  }>({ loading: true, error: null, events: [] });

  useEffect(() => {
    if (!repairId) {
      setState({ loading: false, error: null, events: [] });
      return;
    }
    let cancelled = false;
    setState({ loading: true, error: null, events: [] });
    (async () => {
      const supabase = getSupabase();
      const { data, error } = (await supabase
        .from("repair_status_events")
        .select("*")
        .eq("repair_id", repairId)
        .order("created_at", { ascending: true })) as {
        data: RepairStatusEvent[] | null;
        error: { message: string } | null;
      };
      if (cancelled) return;
      if (error) {
        setState({ loading: false, error: error.message, events: [] });
        return;
      }
      setState({ loading: false, error: null, events: data ?? [] });
    })();
    return () => {
      cancelled = true;
    };
  }, [repairId]);

  return state;
}

/** Create a new repair report against an active tenancy. */
export function useReportRepair() {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(input: {
    tenancy_id: string;
    property_id: string;
    title: string;
    description: string;
    reporter_priority: RepairPriority;
  }): Promise<{ id: string | null; error: string | null }> {
    setSubmitting(true);
    setError(null);
    const supabase = getSupabase();
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) {
      const message = "You must be signed in to report a repair.";
      setError(message);
      setSubmitting(false);
      return { id: null, error: message };
    }
    const { data, error: e } = await supabase
      .from("repairs")
      .insert({
        ...input,
        reporter_user_id: user.id,
      })
      .select("id")
      .single();
    setSubmitting(false);
    if (e) {
      setError(e.message);
      return { id: null, error: e.message };
    }
    return { id: (data as { id: string }).id, error: null };
  }

  return { submit, submitting, error };
}

/** Tenant: request a Help me Fix video-triage magic link for a repair. */
export function useRequestTriageLink() {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function request(repairId: string): Promise<{ link: string | null; error: string | null }> {
    setSubmitting(true);
    setError(null);
    const supabase = getSupabase();
    const { data, error: e } = await supabase.functions.invoke<{ link?: string; error?: string }>(
      "request-triage-link",
      { body: { repair_id: repairId } },
    );
    setSubmitting(false);
    if (e) {
      setError(e.message);
      return { link: null, error: e.message };
    }
    if (data?.error) {
      setError(data.error);
      return { link: null, error: data.error };
    }
    if (!data?.link) {
      setError("No link returned");
      return { link: null, error: "No link returned" };
    }
    return { link: data.link, error: null };
  }

  return { request, submitting, error };
}

/** Admin-only: change a repair's status (also auto-emits a status event via trigger). */
export function useUpdateRepairStatus() {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function update(repairId: string, updates: Partial<RepairAdmin>): Promise<string | null> {
    setSubmitting(true);
    setError(null);
    const supabase = getSupabase();
    const { error: e } = await supabase.from("repairs").update(updates).eq("id", repairId);
    setSubmitting(false);
    if (e) {
      setError(e.message);
      return e.message;
    }
    return null;
  }

  return { update, submitting, error };
}

/** Pretty labels for status / priority. */
export const REPAIR_STATUS_LABEL: Record<RepairStatus, string> = {
  reported: "Reported",
  triaged: "Triaged",
  contractor_assigned: "Contractor assigned",
  in_progress: "In progress",
  completed: "Completed",
  cancelled: "Cancelled",
};

export const REPAIR_PRIORITY_LABEL: Record<RepairPriority, string> = {
  low: "Low",
  medium: "Medium",
  high: "High",
  emergency: "Emergency",
};
